import { ResearchInPracticeSection } from '../components/sections/ResearchInPracticeSection';
import React, { useEffect, useRef } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, ChevronRight } from 'lucide-react';
import { Container, Button } from '../components/ui';
import { ServiceCard } from '../components/sections';
import {
  getWhatWeDoServiceById,
  WHAT_WE_DO_SERVICES,
  type WhatWeDoService,
  type SubOffering,
} from '../data/whatWeDo';

/*
 * ServiceDetail
 * -------------
 * Per-service landing page reached via `/services/<slug>`. The slug is
 * the same id used in the Contact form's SERVICE_OPTIONS and the
 * sidebar drawer, so all three surfaces stay in lock-step through a
 * single `WHAT_WE_DO_SERVICES` list.
 *
 * Layout mirrors the reference the user shared:
 *
 *   ┌────────────────────────────────────────────────────────┐
 *   │  Home > Services > <Service Name>                       │  ← breadcrumbs
 *   ├────────────────────────────────────────────────────────┤
 *   │                                                        │
 *   │              [ full-bleed cover video ]                │
 *   │                                                        │
 *   │             ┌───────────────────────────┐              │
 *   │             │  Service Title            │              │  ← overlay card
 *   │             │  Marketing description…   │
 *   │             │  [ Connect w/ experts →]  │
 *   │             └───────────────────────────┘
 *   │                                                        │
 *   └────────────────────────────────────────────────────────┘
 *
 * Below the hero we render the global `<ConnectSection />` (mounted in
 * Layout.tsx) so the visitor still gets a lead-capture form above the
 * footer — no per-page duplication needed here.
 */

// Base path offset for the fixed site header. Used as a spacer
// height above the breadcrumb bar and as the sticky offset so the
// bar re-latches directly under the header when scrolling.
const HEADER_OFFSET_PX = 80; // matches Header's h-20

export const ServiceDetail: React.FC = () => {
  const { serviceId } = useParams<{ serviceId: string }>();
  const service = getWhatWeDoServiceById(serviceId);

  // Unknown slug — bounce to the /services list. We could also render
  // an inline "service not found" state, but a redirect keeps the URL
  // clean and matches how most catalog pages handle bad ids.
  if (!service) {
    return <Navigate to="/services" replace />;
  }

  return (
    <>
      {/* ═════════════════ Breadcrumb bar ═════════════════
          Sticky just under the fixed header so it re-latches to the
          top of the viewport once the user starts scrolling — the
          user can always see where they are (Home > Services >
          <Service>) without scrolling back to the top.

          A zero-content spacer above it accounts for the 80 px fixed
          header; the sticky bar's `top-20` (80 px) offset then holds
          it flush with the header's bottom edge. `z-40` puts it above
          page content but below the header (z-50). */}
      <div style={{ height: `${HEADER_OFFSET_PX}px` }} aria-hidden />
      <div
        className="sticky top-20 z-40 bg-white border-b border-navy-100 shadow-sm"
      >
        <Container className="py-4">
          <Breadcrumbs currentTitle={service.title} />
        </Container>
      </div>

      {/* ═════════════════ Cover hero ═════════════════
          Full-bleed autoplaying video with an overlay card anchored to
          the bottom of the frame. The overlay uses a small max-width so
          it reads as a "poster" over the video rather than a full-width
          bar (which is what makes the reference feel premium). */}
      <ServiceHero service={service} />

      {/* ═════════════════ Our sub-offerings ═════════════════
          Full-bleed deep-navy section listing the concrete named
          offerings this service ships (Conversational AI, Data
          Visualization, Managed Cloud Ops, etc.), each with a short
          human-voiced description. Every service in the catalog now
          defines a `subOfferings` array — the render is unconditional
          here, but the guard stays in case a future service is added
          without one populated yet. */}
      {service.subOfferings && service.subOfferings.length > 0 && (
        <ServiceSubOfferingsSection subOfferings={service.subOfferings} />
      )}

      {/* ═════════════════ How we work + why we keep learning ═════════════════
          Both paragraphs are pulled from the current service so every
          detail page reads as tailored (IT-consulting scoping calls,
          Salesforce release cadence, AI evaluation-first workflows,
          etc.). Only real numbers appear in the stats — 7 clients,
          >98% CSAT — plus one honest qualitative note ("Ongoing"
          R&D). Includes a bottom CTA that mirrors the hero CTA
          (same slug + inquiry-prefill target). */}
      <ServiceApproachSection service={service} />

      {/* ═════════════════ Explore our other services ═════════════════
          Same visual pattern as the /services listing's "Explore how
          we can help" grid, but filtered to the OTHER 10 services so
          the visitor is nudged toward what else we do without seeing
          the page they're already on. */}
      <ExploreOtherServicesSection currentServiceId={service.id} />
      {/* Show the full research catalog, as on IstaLabs. */}
      <ResearchInPracticeSection />
    </>
  );
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/**
 * Small semantic breadcrumbs component. Kept inline (not a shared UI
 * export) because it's only used on ServiceDetail pages today; if a
 * second page starts using breadcrumbs, promote it to `components/ui`.
 */
const Breadcrumbs: React.FC<{ currentTitle: string }> = ({ currentTitle }) => (
  <nav
    aria-label="Breadcrumb"
    className="flex flex-wrap items-center gap-2 text-sm"
  >
    <Link
      to="/"
      className="text-navy-500 hover:text-accent-600 transition-colors"
    >
      Home
    </Link>
    <ChevronRight className="w-3.5 h-3.5 text-navy-400 shrink-0" aria-hidden />
    <Link
      to="/services"
      className="text-navy-500 hover:text-accent-600 transition-colors"
    >
      Services
    </Link>
    <ChevronRight className="w-3.5 h-3.5 text-navy-400 shrink-0" aria-hidden />
    <span
      aria-current="page"
      className="font-semibold text-navy-900 break-words"
    >
      {currentTitle}
    </span>
  </nav>
);

/**
 * The full-width video hero + overlay card. Extracted so the outer
 * component reads cleanly and the video-play nudge lives next to the
 * <video> element it operates on.
 */
const ServiceHero: React.FC<{ service: WhatWeDoService }> = ({ service }) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);

  // Autoplay + muted is usually enough, but some browsers (Safari, in
  // particular) sit on the first frame until you nudge them. Fires
  // once on mount and again if the underlying URL changes (route
  // navigation between two services without a full remount, though
  // that shouldn't happen thanks to the `key` in ServicePageRouter
  // below — belt and braces).
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = true;
    const p = v.play();
    if (p && typeof p.catch === 'function') p.catch(() => {});
  }, [service.mediaUrl]);

  // CTA target — always /contact with the service pre-selected.
  // `ctaInquiry` (currently only set for AI) adds an additional param
  // that Contact.tsx uses to pre-fill a specific message body.
  // Same helper the bottom CTA uses so the two targets can't drift.
  const contactHref = buildContactHref(service);

  return (
    <section className="relative bg-navy-950">
      {/* Content sets the hero height; the shared minimum is capped on ultrawide screens. */}
      <div
        className="responsive-hero relative w-full overflow-hidden"
      >
        <video
          key={service.mediaUrl}
          ref={videoRef}
          src={service.mediaUrl}
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          className="absolute inset-0 w-full h-full object-cover"
        />
        {/* Even, slightly darker overlay so the centred card reads
            clearly against any frame of the video. The previous
            bottom-heavy gradient made sense when the card sat at the
            bottom of the hero, but with the card centred we want
            uniform contrast across the whole surface. */}
        <div
          aria-hidden
          className="absolute inset-0 bg-black/35"
        />

        {/* Overlay card — centred both horizontally AND vertically
            inside the hero, per user direction. `items-center` handles
            the vertical axis; `justify-center` the horizontal. */}
        <div className="relative flex items-center justify-center px-4 sm:px-6 py-10 sm:py-14 lg:py-20">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className={
              // Wide, prominent card that mirrors the /services hero
              // card so every page in the What-We-Do flow shares the
              // same visual weight. Scales up through the breakpoints.
              'w-full max-w-3xl md:max-w-4xl lg:max-w-5xl xl:max-w-6xl ' +
              'bg-white rounded-2xl border border-navy-100 ' +
              'shadow-[0_25px_70px_-15px_rgba(2,8,20,0.6)] ' +
              'p-5 sm:p-8 md:p-10 lg:p-12'
            }
          >
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-[54px] font-bold text-navy-900 leading-tight mb-4 md:mb-6">
              {service.title}
            </h1>
            <p className="text-base sm:text-lg md:text-xl lg:text-[22px] text-navy-700 leading-relaxed mb-7 md:mb-9">
              {service.heroDescription}
            </p>
            <Link to={contactHref}>
              <Button
                variant="primary"
                size="lg"
                rightIcon={<ArrowRight className="w-5 h-5" />}
              >
                {service.ctaLabel}
              </Button>
            </Link>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

/**
 * "Our sub-offerings" — full-bleed section on the same deep-navy
 * gradient the Contact form and global ConnectSection use, so all
 * three dark surfaces on the site read as one visual family.
 *
 * Layout intent (per client direction):
 *   - Full-bleed (not wrapped in Container) so the grid uses the
 *     entire viewport width instead of feeling squeezed in the middle
 *   - NO icons per row — deliberate; the copy is the content
 *   - NO "Learn more" links per row — again per client direction
 *   - No cards, no drop shadows — just typography + a thin accent
 *     top-rule per row for editorial rhythm
 *   - Auto-flowing responsive grid (1 / 2 / 3 columns) so the section
 *     grows gracefully as more offerings are added
 */
const ServiceSubOfferingsSection: React.FC<{
  subOfferings: readonly SubOffering[];
}> = ({ subOfferings }) => (
  <section
    className="relative overflow-hidden text-white"
    style={{
      // Same deep-navy gradient as the Contact-form section and the
      // global ConnectSection so the three dark surfaces feel unified.
      background:
        'linear-gradient(135deg, #020815 0%, #051124 35%, #0a2148 65%, #071730 100%)',
    }}
  >
    {/* Slowly-drifting orbs for depth — same choreography as the
        other deep-navy sections, low opacity so they read as
        ambient light rather than a wash. */}
    <motion.div
      aria-hidden
      className="pointer-events-none absolute -top-32 -left-24 w-[420px] h-[420px] rounded-full blur-3xl bg-blue-600/20"
      animate={{ x: [0, 60, 0], y: [0, 50, 0] }}
      transition={{ duration: 20, repeat: Infinity, ease: 'easeInOut' }}
    />
    <motion.div
      aria-hidden
      className="pointer-events-none absolute -bottom-36 -right-24 w-[520px] h-[520px] rounded-full blur-3xl bg-indigo-600/20"
      animate={{ x: [0, -60, 0], y: [0, -40, 0] }}
      transition={{ duration: 24, repeat: Infinity, ease: 'easeInOut' }}
    />

    <div className="relative z-10 py-16 md:py-20 lg:py-24 site-wide-container">
      <motion.h2
        className="heading-lg mb-10 md:mb-14"
        initial={{ opacity: 0, y: 12 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-80px' }}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
      >
        Our sub-offerings
      </motion.h2>

      {/* Responsive grid: 1 / 2 / 3 columns. Generous row gap so
          each offering reads as its own module rather than a table
          entry. */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 lg:gap-x-12 gap-y-10 md:gap-y-12">
        {subOfferings.map((offering, i) => (
          <SubOfferingBlock
            key={offering.name}
            offering={offering}
            animationDelay={Math.min(i, 5) * 0.05}
          />
        ))}
      </div>
    </div>
  </section>
);

const SubOfferingBlock: React.FC<{
  offering: SubOffering;
  animationDelay: number;
}> = ({ offering, animationDelay }) => (
  <motion.div
    initial={{ opacity: 0, y: 16 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: '-40px' }}
    transition={{
      duration: 0.45,
      delay: animationDelay,
      ease: [0.22, 1, 0.36, 1],
    }}
  >
    {/* Thin accent top-rule — editorial marker without needing an
        icon or a card. Keeps the layout feeling designed rather
        than dumped into a template. */}
    <div className="h-px bg-white/25 mb-5" aria-hidden />
    <h3 className="text-xl md:text-[22px] font-bold text-white leading-tight mb-3">
      {offering.name}
    </h3>
    <p className="text-[15px] md:text-base text-white/75 leading-relaxed">
      {offering.description}
    </p>
  </motion.div>
);

/**
 * The "How we work with you / Why we keep learning, researching, and
 * innovating" section. Both paragraphs are pulled from the passed
 * `service` so every detail page reads as tailored — IT consultancy
 * talks about scoping calls, Salesforce talks about release cadence,
 * AI talks about evaluation-first proofs of concept, and so on.
 *
 * Stats are the same across pages because they describe the FIRM's
 * track record, not the service. Only real numbers make it into the
 * numeric callouts (7 clients delivered, >98% CSAT); the third row is
 * an honest qualitative note ("Ongoing" R&D) rather than a made-up
 * figure.
 */
const ServiceApproachSection: React.FC<{ service: WhatWeDoService }> = ({
  service,
}) => (
  <section className="bg-white py-16 md:py-24 lg:py-28">
    <Container>
      {/* ────────── First block: how we work (service-specific) ────────── */}
      <motion.div
        className="max-w-4xl mb-14 md:mb-20"
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-80px' }}
        transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
      >
        <h2 className="heading-lg text-navy-900 mb-5 md:mb-6">
          How we work with you
        </h2>
        <p className="text-lg md:text-xl text-navy-700 leading-relaxed">
          {service.approachParagraph}
        </p>
      </motion.div>

      {/* ────────── Stats row (firm-level, three callouts) ──────────
          Three items instead of four — the "Multiple / Active
          engagements today" tile was pulled per user direction.
          Layout collapses to a single column on narrow phones so
          each stat is legible; sm+ spreads them across three
          columns. */}
      <motion.div
        className="grid grid-cols-1 sm:grid-cols-3 gap-x-6 gap-y-10 mb-14 md:mb-24"
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-80px' }}
        transition={{ duration: 0.45, delay: 0.05, ease: [0.22, 1, 0.36, 1] }}
      >
        <Stat number="7" label="Clients we've delivered for" />
        <Stat number=">98%" label="Customer satisfaction score" />
        <Stat number="Ongoing" label="R&D, research, and innovation" />
      </motion.div>

      {/* ────────── Second block: why we keep learning (service-specific) ────────── */}
      <motion.div
        className="max-w-4xl"
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-80px' }}
        transition={{ duration: 0.45, delay: 0.05, ease: [0.22, 1, 0.36, 1] }}
      >
        <h2 className="heading-lg text-navy-900 mb-5 md:mb-6">
          Why we keep learning, researching, and innovating
        </h2>
        <p className="text-lg md:text-xl text-navy-700 leading-relaxed mb-8 md:mb-10">
          {service.learningParagraph}
        </p>

        {/* Second appearance of the hero CTA — same target and label
            as the button on the cover video, mounted here so a
            visitor who scrolled past the hero can take action
            without scrolling back up. Uses the shared
            `buildContactHref` helper so the ?service=&inquiry=
            params stay in lockstep with the hero button. */}
        <Link to={buildContactHref(service)}>
          <Button
            variant="primary"
            size="lg"
            rightIcon={<ArrowRight className="w-5 h-5" />}
          >
            {service.ctaLabel}
          </Button>
        </Link>
      </motion.div>
    </Container>
  </section>
);

/**
 * "Explore our other services" section — same visual pattern as the
 * `/services` listing's "Explore how we can help" grid, but filtered
 * to exclude the service the visitor is currently viewing (so they
 * don't see a card that just says "you are here"). Rendered
 * full-bleed with generous horizontal padding, matching the parent
 * pattern on Services.tsx so both grids look identical.
 */
const ExploreOtherServicesSection: React.FC<{
  currentServiceId: string;
}> = ({ currentServiceId }) => {
  const otherServices = WHAT_WE_DO_SERVICES.filter(
    (s) => s.id !== currentServiceId,
  );

  return (
    <section className="bg-navy-50/60 py-16 md:py-20 lg:py-24">
      <div className="site-wide-container">
        <motion.h2
          className="heading-lg text-navy-900 mb-10 md:mb-14"
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        >
          Explore our other services
        </motion.h2>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 md:gap-x-8 lg:gap-x-10 gap-y-14 md:gap-y-16">
          {otherServices.map((s, i) => (
            <ServiceCard
              key={s.id}
              service={s}
              animationDelay={Math.min(i, 5) * 0.06}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

/**
 * Builds the /contact link target for a service's CTA — used by both
 * the hero button and the mid-page CTA further down the detail page.
 * Kept as a helper so the two callers can't drift out of sync.
 */
function buildContactHref(service: WhatWeDoService): string {
  return (
    `/contact?service=${encodeURIComponent(service.id)}` +
    (service.ctaInquiry
      ? `&inquiry=${encodeURIComponent(service.ctaInquiry)}`
      : '')
  );
}

/**
 * Single stat callout — a thick navy top-rule, an oversized number (or
 * word), and a small muted label. Editorial rather than corporate: no
 * boxed card, no drop shadow, no icons. Handles both digit values
 * ("7", "95%") and word values ("Multiple", "Ongoing") with the same
 * treatment.
 */
const Stat: React.FC<{ number: string; label: string }> = ({
  number,
  label,
}) => (
  <div className="border-t-2 border-navy-900 pt-5">
    <p className="text-4xl md:text-5xl lg:text-[56px] font-bold text-navy-900 mb-3 leading-none">
      {number}
    </p>
    <p className="text-sm md:text-[15px] text-navy-600 leading-relaxed">
      {label}
    </p>
  </div>
);
